import {
  Directive,
  ElementRef,
  inject,
  input,
  InputSignal,
  Renderer2,
  RendererStyleFlags2,
} from '@angular/core';
import { Position } from '../types/position.type';

@Directive({
  selector: '[tooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class Tooltip {
  private el: ElementRef = inject(ElementRef);
  private renderer: Renderer2 = inject(Renderer2);

  readonly tooltip: InputSignal<string | null> = input.required<string | null>();
  readonly tooltipDelay: InputSignal<number> = input<number>(200);
  readonly tooltipDuration: InputSignal<number> = input<number>(5000);
  readonly tooltipPosition: InputSignal<Position> = input<Position>('bottom');

  private readonly VIEWPORT_MARGIN: number = 8;
  private readonly ARROW_EDGE_MARGIN: number = 8;

  private myPopup: HTMLElement | null = null;
  private timer: NodeJS.Timeout | null = null;
  private hideTimer: NodeJS.Timeout | null = null;

  onMouseEnter(): void {
    if (!this.tooltip()) return;
    this.timer = setTimeout(() => {
      this.show();
    }, this.tooltipDelay());
  }

  onMouseLeave(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hide();
  }

  private show(): void {
    if (this.tooltip()) this.create();
    this.setPosition();
    this.renderer.addClass(this.myPopup, 'tooltip-show');

    if (this.tooltipDuration && this.tooltipDuration() > 0) {
      this.hideTimer = setTimeout(() => {
        this.hide();
      }, this.tooltipDuration());
    }
  }

  private hide(): void {
    if (this.myPopup) {
      this.renderer.removeClass(this.myPopup, 'tooltip-show');
      setTimeout(() => {
        if (this.myPopup) {
          this.renderer.removeChild(document.body, this.myPopup);
          this.myPopup = null;
        }
      }, 300);
    }
  }

  private create(): void {
    if (!this.tooltip) return;
    this.myPopup = this.renderer.createElement('span');
    this.renderer.appendChild(this.myPopup, this.renderer.createText(this.tooltip()!));
    this.renderer.appendChild(document.body, this.myPopup);
    this.renderer.addClass(this.myPopup, 'tooltip');
    this.renderer.addClass(this.myPopup, `tooltip-${this.tooltipPosition()}`);
    // Mesure à l'origine pour éviter que le navigateur ne rétrécisse le tooltip
    this.renderer.setStyle(this.myPopup, 'top', '0px');
    this.renderer.setStyle(this.myPopup, 'left', '0px');
  }

  private setPosition(): void {
    const hostPos: any = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos: DOMRect = this.myPopup!.getBoundingClientRect();
    const scrollPos: number =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const scrollX: number = window.scrollX || document.documentElement.scrollLeft || 0;

    let top: number, left: number;

    switch (this.tooltipPosition()) {
      case 'top':
        top = hostPos.top - tooltipPos.height - 10;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      case 'bottom':
        top = hostPos.bottom + 10;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;
      case 'left':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.left - tooltipPos.width - 10;
        break;
      case 'right':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.right + 10;
        break;
      default:
        top = hostPos.bottom + 10;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
    }

    const viewportWidth: number = document.documentElement.clientWidth;
    const viewportHeight: number = document.documentElement.clientHeight;
    const clampedLeft: number = this.clamp(
      left,
      this.VIEWPORT_MARGIN,
      viewportWidth - tooltipPos.width - this.VIEWPORT_MARGIN,
    );
    const clampedTop: number = this.clamp(
      top,
      this.VIEWPORT_MARGIN,
      viewportHeight - tooltipPos.height - this.VIEWPORT_MARGIN,
    );

    // Décale la flèche pour qu'elle pointe toujours le centre de l'élément ciblé -> --- <-
    let arrowOffset: number;
    if (this.tooltipPosition() === 'left' || this.tooltipPosition() === 'right') {
      const maxOffset: number = Math.max(tooltipPos.height / 2 - this.ARROW_EDGE_MARGIN, 0);
      arrowOffset = this.clamp(
        hostPos.top + hostPos.height / 2 - (clampedTop + tooltipPos.height / 2),
        -maxOffset,
        maxOffset,
      );
    } else {
      const maxOffset: number = Math.max(tooltipPos.width / 2 - this.ARROW_EDGE_MARGIN, 0);
      arrowOffset = this.clamp(
        hostPos.left + hostPos.width / 2 - (clampedLeft + tooltipPos.width / 2),
        -maxOffset,
        maxOffset,
      );
    }

    this.renderer.setStyle(this.myPopup, 'top', `${clampedTop + scrollPos}px`);
    this.renderer.setStyle(this.myPopup, 'left', `${clampedLeft + scrollX}px`);
    this.renderer.setStyle(
      this.myPopup,
      '--arrow-offset',
      `${arrowOffset}px`,
      RendererStyleFlags2.DashCase,
    );
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
  }

  ngOnDestroy(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    if (this.myPopup) {
      this.renderer.removeChild(document.body, this.myPopup);
    }
  }
}
